import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Select, MenuItem, FormControl, InputLabel, Checkbox, FormGroup,
  FormControlLabel, IconButton, Typography, Stack, Chip,
  Card, CardContent, Box, Divider,
} from '@mui/material';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

const TR_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

// Monthly status — shown every month
const MONTHLY_STATUS_FIELDS = [
  { key: 'dgs_yapildi',        label: 'DGS Yapıldı' },
  { key: 'puantaj_yapildi',    label: 'Puantaj Yapıldı' },
  { key: 'mali_veri_istendi',  label: 'Mali Veri İstendi' },
  { key: 'raporlar_hazirlandi',label: 'Raporlar Hazırlandı' },
  { key: 'yuklendi',           label: 'Yüklendi' },
  { key: 'imzalandi',          label: 'İmzalandı' },
];

// Yearly status — only displayed when the selected period is May (when the
// annual report cycle runs). Persisted alongside monthly columns in the same
// row; other months simply don't render these checkboxes.
const YEARLY_STATUS_FIELDS = [
  { key: 'rapor_cekildi',       label: 'Rapor Çekildi' },
  { key: 'firmaya_iletildi',    label: 'Firmaya İletildi' },
  { key: 'veri_duzeltmesi_var', label: 'Veri Düzeltmesi Var' },
  { key: 'imza_icin_iletildi',  label: 'İmza için İletildi' },
  { key: 'teslim_edildi',       label: 'Teslim Edildi' },
];

const YEARLY_REPORT_MONTH = 5;

const FILE_TITLES = [
  'Aylık Muafiyet Raporu',
  'DGS Formu',
  'Rapor',
  'SGK Fişleri',
  'Muhtasar',
];

function AdminCompanyTracking() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [notesDraft, setNotesDraft] = useState({});

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState(FILE_TITLES[0]);

  const [filesOpen, setFilesOpen] = useState(false);
  const [filesTarget, setFilesTarget] = useState(null);
  const [filesList, setFilesList] = useState([]);

  const fetchRows = useCallback(() => {
    axios
      .get(`${API_BASE}/companyTracking`, { params: { year, month } })
      .then((res) => {
        setRows(res.data || []);
        const draft = {};
        (res.data || []).forEach((r) => { draft[r.id] = r.notlar || ''; });
        setNotesDraft(draft);
      })
      .catch((err) => console.error('Error fetching tracking:', err));
  }, [year, month]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const updateRow = (id, patch) => {
    axios
      .put(`${API_BASE}/companyTracking/${id}`, patch)
      .then(() => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      })
      .catch((err) => console.error('Error updating tracking:', err));
  };

  const handleStatusToggle = (row, key) => {
    updateRow(row.id, { [key]: row[key] ? 0 : 1 });
  };

  const handleNotesBlur = (row) => {
    const next = notesDraft[row.id] ?? '';
    if ((row.notlar || '') === next) return;
    updateRow(row.id, { notlar: next });
  };

  const openUpload = (row) => {
    setUploadTarget(row);
    setUploadFile(null);
    setUploadTitle(FILE_TITLES[0]);
    setUploadOpen(true);
  };

  const submitUpload = async () => {
    if (!uploadFile || !uploadTarget) return;
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('baslik', uploadTitle);
    try {
      await axios.post(
        `${API_BASE}/companyTracking/${uploadTarget.id}/files`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setUploadOpen(false);
      fetchRows();
    } catch (err) {
      console.error('Error uploading file:', err);
    }
  };

  const openFiles = (row) => {
    setFilesTarget(row);
    setFilesOpen(true);
    axios
      .get(`${API_BASE}/companyTracking/${row.id}/files`)
      .then((res) => setFilesList(res.data || []))
      .catch((err) => console.error('Error fetching files:', err));
  };

  const deleteFile = (fileId) => {
    if (!window.confirm('Bu dosyayı silmek istediğinize emin misiniz?')) return;
    axios
      .delete(`${API_BASE}/companyTracking/files/${fileId}`)
      .then(() => {
        setFilesList((prev) => prev.filter((f) => f.id !== fileId));
        fetchRows();
      })
      .catch((err) => console.error('Error deleting file:', err));
  };

  const downloadFile = (fileId, fileName) => {
    const url = `${API_BASE}/companyTracking/files/${fileId}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const yearOptions = [];
  const currentYear = now.getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 1; y++) yearOptions.push(y);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Firma Takibi</Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
          <InputLabel>Ay</InputLabel>
          <Select label="Ay" value={month} onChange={(e) => setMonth(e.target.value)}>
            {TR_MONTHS.map((m, i) => (
              <MenuItem key={m} value={i + 1}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 100 } }}>
          <InputLabel>Yıl</InputLabel>
          <Select label="Yıl" value={year} onChange={(e) => setYear(e.target.value)}>
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Toplam: {rows.length} firma
        </Typography>
      </Stack>

      {/* Desktop / tablet: classic table */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Firma Adı</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>Rapor Dönemi</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 240 }}>Durum</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Notlar</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Yükle</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.company_name}</TableCell>
                <TableCell>{TR_MONTHS[row.period_month - 1]} {row.period_year}</TableCell>
                <TableCell>
                  <FormGroup>
                    {MONTHLY_STATUS_FIELDS.map((sf) => (
                      <FormControlLabel
                        key={sf.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!row[sf.key]}
                            onChange={() => handleStatusToggle(row, sf.key)}
                          />
                        }
                        label={<Typography variant="body2">{sf.label}</Typography>}
                        sx={{ height: 28 }}
                      />
                    ))}
                  </FormGroup>
                  {month === YEARLY_REPORT_MONTH && (
                    <>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
                        Yıllık Rapor
                      </Typography>
                      <FormGroup>
                        {YEARLY_STATUS_FIELDS.map((sf) => (
                          <FormControlLabel
                            key={sf.key}
                            control={
                              <Checkbox
                                size="small"
                                checked={!!row[sf.key]}
                                onChange={() => handleStatusToggle(row, sf.key)}
                              />
                            }
                            label={<Typography variant="body2">{sf.label}</Typography>}
                            sx={{ height: 28 }}
                          />
                        ))}
                      </FormGroup>
                    </>
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    multiline
                    rows={month === YEARLY_REPORT_MONTH ? 15 : 8}
                    fullWidth
                    size="small"
                    value={notesDraft[row.id] ?? ''}
                    onChange={(e) =>
                      setNotesDraft((prev) => ({ ...prev, [row.id]: e.target.value }))
                    }
                    onBlur={() => handleNotesBlur(row)}
                    placeholder="Firma ile ilgili notlar..."
                  />
                </TableCell>
                <TableCell>
                  <Stack spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => openUpload(row)}
                    >
                      Dosya Yükle
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => openFiles(row)}
                    >
                      Dosyalar
                      {row.file_count ? (
                        <Chip
                          label={row.file_count}
                          size="small"
                          sx={{ ml: 1, height: 18 }}
                        />
                      ) : null}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                  Bu dönem için kayıt yok.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile / small viewports: one card per row */}
      <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {rows.length === 0 && (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            Bu dönem için kayıt yok.
          </Typography>
        )}
        {rows.map((row) => (
          <Card key={row.id}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="h6">{row.company_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {TR_MONTHS[row.period_month - 1]} {row.period_year}
                  </Typography>
                </Box>
                {row.file_count ? (
                  <Chip label={`${row.file_count} dosya`} size="small" color="primary" variant="outlined" />
                ) : null}
              </Stack>

              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary">Aylık Durum</Typography>
              <FormGroup>
                {MONTHLY_STATUS_FIELDS.map((sf) => (
                  <FormControlLabel
                    key={sf.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!row[sf.key]}
                        onChange={() => handleStatusToggle(row, sf.key)}
                      />
                    }
                    label={<Typography variant="body2">{sf.label}</Typography>}
                  />
                ))}
              </FormGroup>

              {month === YEARLY_REPORT_MONTH && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="overline" color="primary">Yıllık Rapor</Typography>
                  <FormGroup>
                    {YEARLY_STATUS_FIELDS.map((sf) => (
                      <FormControlLabel
                        key={sf.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!row[sf.key]}
                            onChange={() => handleStatusToggle(row, sf.key)}
                          />
                        }
                        label={<Typography variant="body2">{sf.label}</Typography>}
                      />
                    ))}
                  </FormGroup>
                </>
              )}

              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary">Notlar</Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                size="small"
                value={notesDraft[row.id] ?? ''}
                onChange={(e) =>
                  setNotesDraft((prev) => ({ ...prev, [row.id]: e.target.value }))
                }
                onBlur={() => handleNotesBlur(row)}
                placeholder="Firma ile ilgili notlar..."
                sx={{ mt: 1 }}
              />

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="contained" size="small" fullWidth onClick={() => openUpload(row)}>
                  Dosya Yükle
                </Button>
                <Button variant="outlined" size="small" fullWidth onClick={() => openFiles(row)}>
                  Dosyalar
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Dosya Yükle
          {uploadTarget && (
            <Typography variant="body2" color="text.secondary">
              {uploadTarget.company_name} — {TR_MONTHS[(uploadTarget.period_month || 1) - 1]} {uploadTarget.period_year}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Başlık</InputLabel>
            <Select
              label="Başlık"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
            >
              {FILE_TITLES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <input
            type="file"
            onChange={(e) => setUploadFile(e.target.files[0] || null)}
            style={{ marginTop: 16, display: 'block' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>İptal</Button>
          <Button onClick={submitUpload} variant="contained" disabled={!uploadFile}>
            Yükle
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={filesOpen} onClose={() => setFilesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Dosyalar
          {filesTarget && (
            <Typography variant="body2" color="text.secondary">
              {filesTarget.company_name} — {TR_MONTHS[(filesTarget.period_month || 1) - 1]} {filesTarget.period_year}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Başlık</TableCell>
                <TableCell>Dosya</TableCell>
                <TableCell>Boyut</TableCell>
                <TableCell>Tarih</TableCell>
                <TableCell align="right">İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filesList.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{f.baslik}</TableCell>
                  <TableCell>{f.file_name}</TableCell>
                  <TableCell>{f.size_bytes ? `${Math.round(f.size_bytes / 1024)} KB` : '-'}</TableCell>
                  <TableCell>
                    {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString('tr-TR') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => downloadFile(f.id, f.file_name)}>
                      İndir
                    </Button>
                    <Button size="small" color="error" onClick={() => deleteFile(f.id)}>
                      Sil
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filesList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                    Henüz dosya yüklenmemiş.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilesOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminCompanyTracking;
